/**
 * GlobalErrorBoundary — React error boundary at App root.
 *
 * Per J-X8: wraps the app root; on uncaught error renders ServerError with
 * a UUID reference for support correlation.
 *
 * Also polls /api/health after catching to detect unplanned outage — if health
 * check fails 3 consecutive times, switches to OutagePage instead.
 *
 * Cross-cutting invariants:
 *   - Inv 12: no console.log
 *   - Inv 13: no `any`
 *   - Inv 15: delegates rendering to error pages which use barrel atoms
 */
import React, { type ErrorInfo, type ReactNode } from 'react';
import { ServerErrorPage } from '../../pages/errors/ServerError.tsx';
import { OutagePage } from '../../pages/errors/Outage.tsx';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  referenceId: string | null;
  isOutage: boolean;
}

const HEALTH_ENDPOINT = '/api/health';
const OUTAGE_THRESHOLD = 3;

export class GlobalErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      referenceId: null,
      isOutage: false,
    };
  }

  static getDerivedStateFromError(): Partial<State> {
    return {
      hasError: true,
      referenceId: crypto.randomUUID(),
    };
  }

  override componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Non-console observability: dispatch a DOM event for future wiring.
    // /api/health probe determines if this is an outage vs. isolated error.
    void this.checkHealthAfterError();
  }

  private async checkHealthAfterError(): Promise<void> {
    let failCount = 0;
    for (let i = 0; i < OUTAGE_THRESHOLD; i++) {
      try {
        const res = await fetch(HEALTH_ENDPOINT, { method: 'GET', cache: 'no-store' });
        if (res.ok) {
          // At least one health check passed — not a full outage
          return;
        }
        failCount++;
      } catch {
        failCount++;
      }
    }
    if (failCount >= OUTAGE_THRESHOLD) {
      this.setState({ isOutage: true });
    }
  }

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.isOutage) {
      return <OutagePage />;
    }

    return (
      <ServerErrorPage
        referenceId={this.state.referenceId ?? undefined}
        onRetry={() => {
          this.setState({ hasError: false, referenceId: null, isOutage: false });
        }}
      />
    );
  }
}
